export type DiffRowType = 'context' | 'added' | 'removed';

export interface DiffRow {
    type: DiffRowType;
    text: string;
    /** Its line number in the before-text. Absent on an added row, which is not in it. */
    beforeNumber?: number;
    /** Its line number in the after-text. Absent on a removed row. */
    afterNumber?: number;
}

export interface DiffDisplayRow extends DiffRow {
    /** How many unchanged lines were omitted directly above this row. */
    skippedAbove?: number;
}

/**
 * Above this many cells the quadratic table is not worth building, and the diff degrades to
 * "all of this went, all of that arrived" for the changed middle. At the cap the table is
 * about 4MB; the texts this renders are code blocks, so it is never approached in practice.
 */
const LCS_CELL_LIMIT = 1_000_000;

/**
 * Rows for the changed middle, once the matching top and bottom have been trimmed away.
 *
 * `offset` is how many lines were trimmed off the top, so the numbers reported are the ones
 * in the original texts rather than in the slice.
 */
const middleRows = (a: string[], b: string[], offset: number): DiffRow[] => {
    const wholesale = (): DiffRow[] => [
        ...a.map((text, i) => ({
            type: 'removed' as const,
            text,
            beforeNumber: offset + i + 1,
        })),
        ...b.map((text, i) => ({
            type: 'added' as const,
            text,
            afterNumber: offset + i + 1,
        })),
    ];

    if (a.length === 0 || b.length === 0 || a.length * b.length > LCS_CELL_LIMIT) {
        return wholesale();
    }

    const n = a.length;
    const m = b.length;
    const width = m + 1;
    // Longest common subsequence lengths, filled from the bottom right so the walk below can
    // read them forwards and emit rows in order.
    const lengths = new Uint32Array((n + 1) * width);
    const at = (i: number, j: number): number => lengths[i * width + j];

    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            lengths[i * width + j] =
                a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
        }
    }

    const rows: DiffRow[] = [];
    let i = 0;
    let j = 0;

    while (i < n && j < m) {
        if (a[i] === b[j]) {
            rows.push({
                type: 'context',
                text: a[i],
                beforeNumber: offset + i + 1,
                afterNumber: offset + j + 1,
            });
            i++;
            j++;
        } else if (at(i + 1, j) >= at(i, j + 1)) {
            // Removals lead a replaced line, which is the order a reader expects: the old
            // line, then the new one.
            rows.push({ type: 'removed', text: a[i], beforeNumber: offset + i + 1 });
            i++;
        } else {
            rows.push({ type: 'added', text: b[j], afterNumber: offset + j + 1 });
            j++;
        }
    }

    while (i < n) {
        rows.push({ type: 'removed', text: a[i], beforeNumber: offset + i + 1 });
        i++;
    }

    while (j < m) {
        rows.push({ type: 'added', text: b[j], afterNumber: offset + j + 1 });
        j++;
    }

    return rows;
};

/**
 * An empty text is no lines rather than one blank one, which is what `split` would give.
 * Otherwise a first draft reads as a removed blank line above everything it added.
 */
const toLines = (text: string): string[] => (text === '' ? [] : text.split('\n'));

/**
 * Every line of both texts, in reading order, each marked as unchanged, added or removed.
 *
 * The matching top and bottom are trimmed before the quadratic part runs, which is what keeps
 * a small edit to a long document cheap.
 */
export const diffLines = (before: string, after: string): DiffRow[] => {
    const a = toLines(before);
    const b = toLines(after);

    let start = 0;
    while (start < a.length && start < b.length && a[start] === b[start]) {
        start++;
    }

    let endA = a.length;
    let endB = b.length;
    while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
        endA--;
        endB--;
    }

    const rows: DiffRow[] = [];

    for (let i = 0; i < start; i++) {
        rows.push({ type: 'context', text: a[i], beforeNumber: i + 1, afterNumber: i + 1 });
    }

    rows.push(...middleRows(a.slice(start, endA), b.slice(start, endB), start));

    for (let i = endA; i < a.length; i++) {
        rows.push({
            type: 'context',
            text: a[i],
            beforeNumber: i + 1,
            afterNumber: i - endA + endB + 1,
        });
    }

    return rows;
};

/**
 * The same rows with the untouched stretches dropped, keeping `context` lines either side of
 * every change so a hunk still reads in context.
 *
 * Empty when nothing changed: a diff of two identical texts has nothing to show, and saying so
 * is the caller's job rather than this function's.
 */
export const diffHunks = (before: string, after: string, context = 3): DiffDisplayRow[] => {
    const rows = diffLines(before, after);
    const keep = new Array<boolean>(rows.length).fill(false);

    rows.forEach((row, index) => {
        if (row.type === 'context') {
            return;
        }

        const from = Math.max(0, index - context);
        const to = Math.min(rows.length - 1, index + context);

        for (let i = from; i <= to; i++) {
            keep[i] = true;
        }
    });

    const display: DiffDisplayRow[] = [];
    let skipped = 0;

    rows.forEach((row, index) => {
        if (!keep[index]) {
            skipped++;
            return;
        }

        display.push(skipped > 0 ? { ...row, skippedAbove: skipped } : row);
        skipped = 0;
    });

    return display;
};
